import { useMemo } from "react";
import {
  Cloud,
  Sun,
  CloudRain,
  CloudLightning,
  CloudSnow,
  Wind,
  CloudFog,
  CloudSun,
  MapPin,
} from "lucide-react";

import type { WeatherCardData } from "@/api/message/common/MessageParser";

interface WeatherCardProps {
  data: WeatherCardData;
}

export function WeatherCard({ data }: WeatherCardProps) {
  const current = data.days[0];
  const forecasts = data.days;

  const parseTempRange = (range: string) => {
    const matches = range.match(/(-?\d+)/g);
    if (matches && matches.length >= 2) {
      const v1 = parseInt(matches[0]);
      const v2 = parseInt(matches[1]);
      return { low: Math.min(v1, v2), high: Math.max(v1, v2) };
    }
    return { low: 0, high: 0 };
  };

  const { minGlobal, maxGlobal } = useMemo(() => {
    let min = 100;
    let max = -100;
    data.days.forEach((d) => {
      const { low, high } = parseTempRange(d.tempRange);
      if (low < min) min = low;
      if (high > max) max = high;
    });
    return { minGlobal: min, maxGlobal: max };
  }, [data]);

  const currentTemps = parseTempRange(current.tempRange);

  const getWeatherIcon = (
    _iconName: string,
    weather: string,
    className: string
  ) => {
    const lowerWeather = weather.toLowerCase();
    const iconClass = `${className}`;

    if (lowerWeather.includes("晴"))
      return <Sun className={iconClass + " text-orange-500"} />;
    if (lowerWeather.includes("雨"))
      return <CloudRain className={iconClass + " text-blue-400"} />;
    if (lowerWeather.includes("雷"))
      return <CloudLightning className={iconClass + " text-purple-500"} />;
    if (lowerWeather.includes("雪"))
      return <CloudSnow className={iconClass + " text-blue-200"} />;
    if (lowerWeather.includes("阴"))
      return <Cloud className={iconClass + " text-gray-400"} />;
    if (lowerWeather.includes("云"))
      return <CloudSun className={iconClass + " text-yellow-400"} />;
    if (lowerWeather.includes("风"))
      return <Wind className={iconClass + " text-blue-300"} />;
    if (lowerWeather.includes("雾") || lowerWeather.includes("尘"))
      return <CloudFog className={iconClass + " text-yellow-600"} />;

    return <CloudSun className={iconClass + " text-orange-400"} />;
  };

  const formatDateDisplay = (dateStr: string, index: number) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dateText = `${month}/${day}`;

    if (index === 0) return { week: "今天", date: dateText };
    if (index === 1) return { week: "明天", date: dateText };

    const days = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
    return { week: days[date.getDay()], date: dateText };
  };

  const headerDate = new Date(data.days[0].date);
  const headerDateStr = `${headerDate.getFullYear()}/${
    headerDate.getMonth() + 1
  }/${headerDate.getDate()} ${
    ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][
      headerDate.getDay()
    ]
  }`;

  return (
    <div className="flex justify-center items-start px-1 font-sans">
      <div className="w-full max-w-[25rem] bg-white rounded-2xl border-2 border-gray-100 px-1">
        {/* 顶部：日期与城市 */}
        <div className="px-5 pt-4 pb-2 flex justify-between items-end border-b border-gray-100">
          <div className="text-gray-500 text-sm font-medium">
            {headerDateStr}
          </div>
          <div className="flex items-center text-gray-600 text-sm font-bold gap-1">
            <MapPin className="w-4 h-4" />
            {data.city}
          </div>
        </div>

        {/* --- 顶部核心天气区域 --- */}
        <div className="px-6 pt-6 pb-2 flex justify-between items-center">
          {/* 左侧组合：大数字 + 右侧详情列 */}
          <div className="flex items-center">
            {/* 大数字 */}
            <span className="text-[4rem] leading-none font-normal text-gray-900 -ml-1 -tracking-wide">
              {currentTemps.high}
            </span>
            <div className="flex flex-col ml-3 h-full justify-between">
              <span className="text-[1.25rem] text-gray-400 font-light self-start">
                °C
              </span>
              <span className="text-base text-gray-800 font-medium whitespace-nowrap tracking-tight self-end">
                {currentTemps.low}-{currentTemps.high}°
              </span>
            </div>
          </div>
          {/* 右侧：图标与天气文字 */}
          <div className="flex flex-col items-center mr-1">
            {getWeatherIcon(
              current.icon,
              current.weather,
              "w-[1.5rem] h-[1.5rem] mb-2"
            )}
            <span className="text-gray-800 font-bold text-base text-[0.75rem]">
              {current.weather}
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2 px-6">
          <div className="bg-[#E3F8FF] text-sm px-2 py-1 mb-2 text-color-[#0f0f0f] text-medium rounded-md whitespace-nowrap">
            {current.wind + current.windSpeed}
          </div>
        </div>
        <div className="h-px bg-gray-100 mb-2"></div>

        {/* 列表区域 (保持原有样式) */}
        <div className="px-2 pb-6 space-y-4">
          {forecasts.map((day, index) => {
            const { low, high } = parseTempRange(day.tempRange);
            const { week, date: dateText } = formatDateDisplay(day.date, index);

            const totalRange = maxGlobal - minGlobal;
            const safeRange = totalRange === 0 ? 1 : totalRange;

            const leftPercent = ((low - minGlobal) / safeRange) * 100;
            const widthPercent = ((high - low) / safeRange) * 100;

            return (
              <div key={index} className="flex items-center h-8">
                {/* 1. 日期列 */}
                <div className="w-24 flex items-center gap-2 text-sm">
                  <span className="font-bold text-gray-800">{week}</span>
                  <span className="text-gray-400 text-xs font-medium">
                    {dateText}
                  </span>
                </div>

                {/* 2. 图标与文字 */}
                <div className="w-24 flex items-center gap-2">
                  {getWeatherIcon(day.icon, day.weather, "w-5 h-5")}
                  <span className="text-sm text-gray-700 font-medium truncate">
                    {day.weather}
                  </span>
                </div>

                {/* 3. 进度条 */}
                <div className="flex-1 flex items-center gap-2">
                  <span className="w-6 text-right text-sm text-gray-600 font-medium">
                    {low}°
                  </span>

                  <div className="flex-1 h-1 bg-gray-100 rounded-full relative mx-1">
                    <div
                      className="absolute h-full rounded-full opacity-80"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        background:
                          "linear-gradient(to right, #82D0D1, #A3CF92)",
                      }}
                    ></div>
                  </div>
                  <span className="w-6 text-left text-sm text-gray-600 font-medium">
                    {high}°
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
